import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  TrendingUp,
  Globe,
  Building2,
  Users,
  BarChart3,
  ArrowRight,
  ChevronRight,
  Calendar,
  ExternalLink,
  Star,
  Loader2
} from 'lucide-react';
import { markets, formatCurrency } from '../data/mockData';
import MacroWidgetExtended from '../components/MacroWidgetExtended';
import { ValoramapScoreBadge } from '../components/ValoramapScore';
import axios from 'axios';

const PUBLIC_API_URL = 'https://api-ovh.alainponcelas.com/api';

const MarketsPage = ({ user }) => {
  const { marketCode } = useParams();
  const selectedMarket = marketCode ? markets.find(m => m.code.toLowerCase() === marketCode.toLowerCase()) : null;

  // Market details data (will be replaced by backend)
  const marketDetails = {
    VN: {
      description: "Le Vietnam est l'une des économies à la croissance la plus rapide au monde. Avec un PIB en hausse de 7%+ et l'upgrade FTSE Russell prévu en septembre 2026, le marché vietnamien offre des opportunités exceptionnelles.",
      keyFacts: [
        { label: 'Population', value: '100 millions' },
        { label: 'Âge médian', value: '32 ans' },
        { label: 'Croissance GDP 2026', value: '7.2%' },
        { label: 'Nombre d\'actions', value: '1,800+' }
      ],
      catalysts: [
        'Upgrade FTSE Russell (Frontier → Emerging) - Septembre 2026',
        'Inflows attendus de $3-5 milliards',
        'Alternative manufacturière à la Chine',
        'Forte classe moyenne en expansion'
      ],
      sectors: ['Technology', 'Finance', 'Real Estate', 'Consumer Goods', 'Industrial']
    },
    NSE: {
      description: "L'Inde est devenue la 5ème économie mondiale. Avec 1,45 milliard d'habitants et une croissance GDP de 7%+, le marché indien offre une profondeur et une diversité incomparables.",
      keyFacts: [
        { label: 'Population', value: '1.45 milliards' },
        { label: 'PIB', value: '$3.5 trillions' },
        { label: 'Croissance GDP 2026', value: '7.3%' },
        { label: 'Nombre d\'actions', value: '2,000+' }
      ],
      catalysts: [
        'Plus grande population mondiale',
        'Transformation digitale accélérée (UPI, fintech)',
        'Programmes PLI et Make in India',
        'Consommation domestique robuste'
      ],
      sectors: ['Technology', 'Finance', 'Healthcare', 'Consumer Goods', 'Energy']
    },
    PSE: {
      description: "Les Philippines sont la 2ème économie à la croissance la plus rapide d'ASEAN après le Vietnam. Avec une population jeune et une politique monétaire accommodante, le marché philippin offre des valorisations attractives.",
      keyFacts: [
        { label: 'Population', value: '114 millions' },
        { label: 'Remittances', value: '$35+ milliards/an' },
        { label: 'Croissance GDP 2026', value: '5.4%' },
        { label: 'Nombre d\'actions', value: '270+' }
      ],
      catalysts: [
        'Assouplissement monétaire (taux ↓4.5%)',
        'Inflation faible (1.6%)',
        'Démographie jeune favorable',
        'Fort flux de remittances'
      ],
      sectors: ['Finance', 'Real Estate', 'Consumer Goods', 'Telecommunications']
    },
    JK: {
      description: "L'Indonésie est la plus grande économie d'ASEAN avec 280 millions d'habitants. Riche en ressources naturelles, le marché indonésien offre une exposition unique aux commodities et à la consommation émergente.",
      keyFacts: [
        { label: 'Population', value: '280 millions' },
        { label: 'PIB', value: '$1.4 trillions' },
        { label: 'Croissance GDP 2026', value: '5.3%' },
        { label: 'Nombre d\'actions', value: '800+' }
      ],
      catalysts: [
        'Nouvelle présidence ambitieuse (8% GDP target)',
        'Méga-projet IKN (nouvelle capitale)',
        'Leader mondial du nickel',
        'Classe moyenne en forte croissance'
      ],
      sectors: ['Finance', 'Mining', 'Consumer Goods', 'Telecommunications', 'Energy']
    },
    WSE: {
      description: "La Pologne est le champion de la croissance en Europe de l'Est. Avec une croissance 3x supérieure à la zone euro et des fonds UE massifs, le marché polonais offre une diversification européenne unique.",
      keyFacts: [
        { label: 'Population', value: '38 millions' },
        { label: 'Membre UE', value: 'Depuis 2004' },
        { label: 'Croissance GDP 2026', value: '3.5%' },
        { label: 'Nombre d\'actions', value: '400+' }
      ],
      catalysts: [
        'Leader croissance UE (3x moyenne zone euro)',
        'Fonds RRF massifs (deadline 2026)',
        'Investissements énergie et défense',
        'Baisses de taux anticipées'
      ],
      sectors: ['Finance', 'Energy', 'Technology', 'Retail']
    },
    KRX: {
      description: "La Corée du Sud est un géant technologique mondial. Malgré une croissance plus modérée, le 'Korea Discount' offre des valorisations attractives sur des champions tech comme Samsung et SK Hynix.",
      keyFacts: [
        { label: 'Population', value: '52 millions' },
        { label: 'PIB', value: '$1.8 trillions' },
        { label: 'Croissance GDP 2026', value: '2.1%' },
        { label: 'Nombre d\'actions', value: '2,200+' }
      ],
      catalysts: [
        'Demande semiconducteurs AI en hausse',
        'Korea Discount = valorisations attractives',
        'Nouvel accord commercial US ($350B)',
        'Relance fiscale (+8.1% budget 2026)'
      ],
      sectors: ['Technology', 'Semiconductors', 'Automotive', 'Finance', 'Entertainment']
    }
  };

  // State for API data
  const [topCapStocks, setTopCapStocks] = useState([]);
  const [topScoreStocks, setTopScoreStocks] = useState([]);
  const [stocksLoading, setStocksLoading] = useState(false);

  // Fetch stocks from API when market is selected
  useEffect(() => {
    const fetchMarketStocks = async () => {
      if (!selectedMarket) return;
      
      setStocksLoading(true);
      try {
        // Fetch top 5 by market cap
        const capResponse = await axios.get(`${PUBLIC_API_URL}/public/stocks/screen`, {
          params: { 
            country: selectedMarket.code, 
            sort: 'market_cap', 
            order: 'desc', 
            limit: 5 
          }
        });
        const capStocks = (capResponse.data?.stocks || []).map(s => ({
          symbol: s.symbol,
          name: s.name,
          market: s.market,
          price: s.price,
          currency: s.currency,
          change: s.change ?? null,
          marketCap: s.market_cap,
          valoramap_score: s.valoramap_score
        }));
        setTopCapStocks(capStocks);

        // Fetch top 5 by VALORAMAP score
        const scoreResponse = await axios.get(`${PUBLIC_API_URL}/public/stocks/screen`, {
          params: { 
            country: selectedMarket.code, 
            sort: 'valoramap_score', 
            order: 'desc', 
            limit: 5 
          }
        });
        const scoreStocks = (scoreResponse.data?.stocks || []).map(s => ({
          symbol: s.symbol,
          name: s.name,
          market: s.market,
          price: s.price,
          currency: s.currency,
          change: s.change ?? null,
          valoramap_score: s.valoramap_score
        }));
        setTopScoreStocks(scoreStocks);
      } catch (error) {
        console.error('Failed to fetch market stocks:', error);
        setTopCapStocks([]);
        setTopScoreStocks([]);
      } finally {
        setStocksLoading(false);
      }
    };

    fetchMarketStocks();
  }, [selectedMarket]);

  // Market overview view (no specific market selected)
  if (!selectedMarket) {
    return (
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 mb-4">
              6 Marchés Émergents
            </Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Explorez les marchés à forte croissance
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Accédez aux économies avec la croissance GDP moyenne de 6,4% vs 1,5% pour la zone euro.
            </p>
          </div>

          {/* Markets Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {markets.map((market) => {
              const details = marketDetails[market.code];
              return (
                <Link key={market.code} to={`/markets/${market.code.toLowerCase()}`}>
                  <Card className="h-full bg-white/5 border-white/10 hover:border-emerald-500/30 transition-all card-hover cursor-pointer">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-4xl">{market.flag}</span>
                        <div>
                          <h3 className="text-xl font-bold text-white">{market.name}</h3>
                          <p className="text-sm text-gray-500">{market.exchange}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="p-3 bg-white/5 rounded-lg">
                          <div className="text-2xl font-bold text-emerald-400">{market.gdpGrowth}</div>
                          <div className="text-xs text-gray-500">Croissance GDP</div>
                        </div>
                        <div className="p-3 bg-white/5 rounded-lg">
                          <div className="text-2xl font-bold text-white">{market.stocks.toLocaleString()}</div>
                          <div className="text-xs text-gray-500">Actions</div>
                        </div>
                      </div>

                      <p className="text-sm text-gray-400 line-clamp-2 mb-4">
                        {details?.description.substring(0, 120)}...
                      </p>

                      <div className="flex items-center text-emerald-400 text-sm font-medium">
                        Explorer ce marché
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          {/* Vietnam Highlight */}
          <Card className="mt-12 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/20">
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <div className="lg:w-2/3">
                  <Badge className="bg-amber-500/20 text-amber-400 border-0 mb-4">
                    <Calendar className="w-3 h-3 mr-1" />
                    Septembre 2026
                  </Badge>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                    Vietnam : L'upgrade qui va tout changer
                  </h2>
                  <p className="text-gray-400 mb-6">
                    Le Vietnam va passer de Frontier à Secondary Emerging Market dans l'indice FTSE Russell. 
                    Cela signifie $3-5 milliards d'inflows automatiques des fonds qui trackent cet indice. 
                    Positionnez-vous avant les institutionnels.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link to="/markets/vn">
                      <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                        Explorer le Vietnam
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                    <Link to="/screener">
                      <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                        Voir le screener
                      </Button>
                    </Link>
                  </div>
                </div>
                <div className="lg:w-1/3 text-center">
                  <div className="text-8xl mb-2">🇻🇳</div>
                  <div className="text-5xl font-bold text-emerald-400">+7.2%</div>
                  <div className="text-gray-400">Croissance GDP 2026</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Individual market detail view
  const details = marketDetails[selectedMarket.code];

  return (
    <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
          <Link to="/markets" className="hover:text-emerald-400 transition-colors">
            Marchés
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white">{selectedMarket.name}</span>
        </div>

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8">
          <div className="flex items-center gap-4">
            <span className="text-6xl">{selectedMarket.flag}</span>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white">
                {selectedMarket.name}
              </h1>
              <p className="text-gray-400">{selectedMarket.exchange}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Link to={`/screener?country=${selectedMarket.code}`}>
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                <BarChart3 className="w-4 h-4 mr-2" />
                Screener {selectedMarket.name}
              </Button>
            </Link>
          </div>
        </div>

        {/* Key Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {details?.keyFacts.map((fact, index) => (
            <Card key={index} className="bg-white/5 border-white/10">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-white mb-1">{fact.value}</div>
                <div className="text-sm text-gray-500">{fact.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Macro Trends Widget */}
        <div className="mb-8">
          <MacroWidgetExtended 
            countryCode={selectedMarket.code} 
            countryName={selectedMarket.name}
          />
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-emerald-400" />
                  Vue d'ensemble
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300 leading-relaxed">{details?.description}</p>
              </CardContent>
            </Card>

            {/* Catalysts */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                  Catalyseurs 2026
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {details?.catalysts.map((catalyst, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-emerald-400 text-sm font-medium">{index + 1}</span>
                      </div>
                      <span className="text-gray-300">{catalyst}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Top Stocks by Market Cap */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-emerald-400" />
                  Plus grandes capitalisations
                </CardTitle>
                <Link to={`/screener?country=${selectedMarket.code}`}>
                  <Button variant="ghost" size="sm" className="text-emerald-400 hover:text-emerald-300">
                    Voir tout
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {stocksLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
                  </div>
                ) : topCapStocks.length > 0 ? (
                  <div className="space-y-3">
                    {topCapStocks.map((stock) => (
                      <Link 
                        key={stock.symbol}
                        to={`/stock/${stock.market}-${stock.symbol}`}
                        className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        <div>
                          <div className="font-medium text-white">{stock.symbol}</div>
                          <div className="text-sm text-gray-500 truncate max-w-[150px]">{stock.name}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-white font-medium">
                            {formatCurrency(stock.price, stock.currency)}
                          </div>
                          <div className={`text-sm ${
                            stock.change == null ? 'text-gray-500' :
                            stock.change >= 0 ? 'text-emerald-400' : 'text-red-400'
                          }`}>
                            {stock.change == null ? '—' : `${stock.change >= 0 ? '+' : ''}${stock.change.toFixed(1)}%`}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Aucune donnée disponible</p>
                )}
              </CardContent>
            </Card>

            {/* Top Stocks by VALORAMAP Score */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#00ff88]" />
                  Meilleurs scores VALORAMAP
                </CardTitle>
                <Link to={`/screener?country=${selectedMarket.code}`}>
                  <Button variant="ghost" size="sm" className="text-emerald-400 hover:text-emerald-300">
                    Voir tout
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {stocksLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
                  </div>
                ) : topScoreStocks.length > 0 ? (
                  <div className="space-y-3">
                    {topScoreStocks.map((stock) => (
                      <Link 
                        key={stock.symbol}
                        to={`/stock/${stock.market}-${stock.symbol}`}
                        className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <ValoramapScoreBadge score={stock.valoramap_score} size="sm" />
                          <div>
                            <div className="font-medium text-white">{stock.symbol}</div>
                            <div className="text-sm text-gray-500 truncate max-w-[120px]">{stock.name}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-white font-medium">
                            {formatCurrency(stock.price, stock.currency)}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Aucune donnée disponible</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Sectors */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg">Secteurs clés</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {details?.sectors.map((sector, index) => (
                    <Badge 
                      key={index} 
                      variant="outline" 
                      className="border-white/20 text-gray-300"
                    >
                      {sector}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg">Ressources</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <a 
                  href="#" 
                  className="flex items-center gap-2 text-gray-400 hover:text-emerald-400 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Guide investir {selectedMarket.name}</span>
                </a>
                <a 
                  href="#" 
                  className="flex items-center gap-2 text-gray-400 hover:text-emerald-400 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Analyse macro économique</span>
                </a>
                <a 
                  href="#" 
                  className="flex items-center gap-2 text-gray-400 hover:text-emerald-400 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Calendrier earnings</span>
                </a>
              </CardContent>
            </Card>

            {/* CTA - Hidden for users with active subscription */}
            {(!user || !user.has_access) && (
              <Card className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border-emerald-500/30">
                <CardContent className="p-6 text-center">
                  <h3 className="text-lg font-bold text-white mb-2">
                    Prêt à investir ?
                  </h3>
                  <p className="text-sm text-gray-400 mb-4">
                    Découvrez les opportunités sous-valorisées sur le marché {selectedMarket.name.toLowerCase()}.
                  </p>
                  <Link to="/pricing">
                    <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
                      Essai 7 jours - 9 CHF
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketsPage;
